import { useState, useEffect, useCallback } from 'react';
import {
  getDocumentTypes, getApplications, listDocuments,
  createDocument, deleteDocument, getDocumentStatus, getErrorMessage,
} from '../../api/documents';
import { useAuth } from '../../context/AuthContext';

// Import des documents — réservé au Super Administrateur.
// Types "fichier" (PDF, mode opérateur, GRAFCET, logigramme, .PCS) → upload.
// Type "lien" (Crouzet Soft) → simple URL de téléchargement.
export default function DocumentImportPanel() {
  const { token } = useAuth();

  const [docTypes, setDocTypes] = useState([]);
  const [applications, setApplications] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');

  const [docType, setDocType] = useState('');
  const [title, setTitle] = useState('');
  const [application, setApplication] = useState('');
  const [externalLink, setExternalLink] = useState('');
  const [file, setFile] = useState(null);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [maxSizeBytes, setMaxSizeBytes] = useState(20 * 1024 * 1024);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback((background = false) => {
    if (!background) setLoading(true);
    Promise.all([getDocumentTypes(token), getApplications(token), listDocuments(token), getDocumentStatus(token)])
      .then(([types, apps, docs, status]) => {
        setDocTypes(types);
        setApplications(apps);
        setDocuments(docs);
        setConnected(true);
        setMaxSizeBytes(status.max_size_bytes);
        setListError('');
        if (types.length > 0) setDocType((prev) => prev || types[0].value);
      })
      .catch((err) => {
        setConnected(false);
        setListError(getErrorMessage(err, 'Backend déconnecté — opérations désactivées.'));
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    load();
    const refresh = setInterval(() => load(true), 5000);
    return () => clearInterval(refresh);
  }, [load]);

  const currentTypeInfo = docTypes.find((t) => t.value === docType);
  const isLinkType = currentTypeInfo?.kind === 'link';

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    if (!title.trim()) {
      setFormError('Veuillez indiquer un titre.');
      return;
    }
    if (isLinkType && !externalLink.trim()) {
      setFormError('Veuillez indiquer le lien de téléchargement.');
      return;
    }
    if (!isLinkType && !file) {
      setFormError('Veuillez sélectionner un fichier.');
      return;
    }
    if (file && file.size > maxSizeBytes) {
      setFormError(`Le fichier dépasse la limite de ${Math.round(maxSizeBytes / 1024 / 1024)} Mo.`);
      return;
    }
    if (!connected) {
      setFormError('Backend déconnecté — import désactivé.');
      return;
    }

    setSubmitting(true);
    setUploadProgress(0);
    try {
      await createDocument(token, {
        docType,
        title: title.trim(),
        application: application || null,
        externalLink: isLinkType ? externalLink.trim() : null,
        file: isLinkType ? null : file,
      }, setUploadProgress);
      setFormSuccess(`Document "${title.trim()}" importé avec succès.`);
      setTitle('');
      setExternalLink('');
      setFile(null);
      setUploadProgress(100);
      load();
    } catch (err) {
      setFormError(getErrorMessage(err, "Impossible d'importer ce document."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!connected || deletingId !== null) return;
    if (!window.confirm(`Supprimer le document "${doc.title}" ?`)) return;
    setDeletingId(doc.id);
    try {
      await deleteDocument(token, doc.id);
      load();
    } catch (err) {
      setListError(getErrorMessage(err, 'Impossible de supprimer ce document.'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="admin-grid">
      <div className="admin-card">
        <h2>Importer un document</h2>
        <div className={connected ? 'document-status connected' : 'document-status disconnected'}>
          {connected ? 'BACKEND CONNECTÉ — SOURCE : SERVEUR' : 'BACKEND DÉCONNECTÉ — OPÉRATIONS DÉSACTIVÉES'}
        </div>
        <form onSubmit={handleCreate} className="auth-field-group">
          {formError && <div className="auth-error">{formError}</div>}
          {formSuccess && <div className="auth-success">{formSuccess}</div>}

          <div className="auth-field">
            <label htmlFor="doc-type">Type de document</label>
            <select id="doc-type" value={docType} onChange={(e) => setDocType(e.target.value)}>
              {docTypes.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div className="auth-field">
            <label htmlFor="doc-title">Titre</label>
            <input
              id="doc-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="ex : Cahier des charges — Feux Tricolores"
            />
          </div>

          <div className="auth-field">
            <label htmlFor="doc-app">Application liée (optionnel)</label>
            <select id="doc-app" value={application} onChange={(e) => setApplication(e.target.value)}>
              <option value="">— Aucune —</option>
              {applications.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </div>

          {isLinkType ? (
            <div className="auth-field">
              <label htmlFor="doc-link">Lien de téléchargement</label>
              <input
                id="doc-link"
                type="url"
                value={externalLink}
                onChange={(e) => setExternalLink(e.target.value)}
                placeholder="https://…"
              />
            </div>
          ) : (
            <div className="auth-field">
              <label htmlFor="doc-file">Fichier (PDF, PCS, image)</label>
              <input
                id="doc-file"
                type="file"
                accept=".pdf,.pcs,.png,.jpg,.jpeg"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                disabled={!connected || submitting}
              />
              <small>Formats historiques conservés — taille maximale {Math.round(maxSizeBytes / 1024 / 1024)} Mo.</small>
            </div>
          )}

          {submitting && <div className="document-progress" aria-live="polite">Envoi : {uploadProgress}%</div>}
          <button type="submit" className="btn-primary" disabled={submitting || !connected}>
            {submitting ? 'Import…' : 'Importer'}
          </button>
        </form>
      </div>

      <div className="admin-card">
        <h2>Documents importés</h2>
        {listError && <div className="auth-error">{listError}</div>}

        {loading ? (
          <p className="admin-empty">Chargement…</p>
        ) : documents.length === 0 ? (
          <p className="admin-empty">Aucun document importé.</p>
        ) : (
          <div className="documents-table-wrap">
          <table className="admin-table documents-table">
            <thead>
              <tr>
                <th>Titre</th>
                <th>Type</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {documents.map((d) => (
                <tr key={d.id}>
                  <td>{d.title}</td>
                  <td><span className="role-badge role-niveau">{d.doc_type_label}</span></td>
                  <td>
                    <button className="btn-danger-link" disabled={!connected || deletingId !== null} onClick={() => handleDelete(d)}>
                      {deletingId === d.id ? 'Suppression…' : 'Supprimer'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
